import { dayjs } from 'src/lib/dayjs'

export const today = new Date()
today.setHours(0, 0, 0, 0)

const dayFmt = new Intl.DateTimeFormat('en', { weekday: 'short' })

export class CalendarDate {
  public date: number
  public day: string
  public hour: number
  public weekNumber: number
  public isSibling = false
  public isToday: boolean
  public isTomorrow: boolean
  public thisWeek: boolean
  public nextWeek: boolean
  constructor(public obj: Date) {
    this.day = dayFmt.format(obj)
    this.date = obj.getDate()
    this.weekNumber = weekNumber(obj)
    this.isToday =
      obj.getDate() === today.getDate() &&
      obj.getMonth() === today.getMonth() &&
      obj.getFullYear() === today.getFullYear()

    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)
    this.isTomorrow =
      obj.getDate() === tomorrow.getDate() &&
      obj.getMonth() === tomorrow.getMonth() &&
      obj.getFullYear() === tomorrow.getFullYear()

    this.thisWeek = obj.getFullYear() === today.getFullYear() && this.weekNumber === weekNumber(today)

    const weekFromToday = new Date(today)
    weekFromToday.setDate(today.getDate() + 7)
    this.nextWeek = obj.getFullYear() === weekFromToday.getFullYear() && this.weekNumber === weekNumber(weekFromToday)
  }
}

export function weekNumber(date: Date) {
  const target = new Date(date.valueOf())
  const dayNr = (date.getDay() + 6) % 7
  target.setDate(target.getDate() - dayNr + 3)
  const firstThursday = target.valueOf()
  target.setMonth(0, 1)
  if (target.getDay() !== 4) {
    target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7))
  }
  return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000)
}

export function timeOfDay(date: Date) {
  const hour = date.getHours()
  if (hour < 12) {
    return 'morning'
  } else if (hour > 17) {
    return 'evening'
  } else {
    return 'afternoon'
  }
}
export function isWeekday(date: Date) {
  return date.getDay() !== 0 && date.getDay() !== 6
}

export function closestWeekday(date: Date) {
  const currentDate = new Date(date)
  if (isWeekday(currentDate)) {
    return currentDate
  } else {
    while (!isWeekday(currentDate)) {
      currentDate.setDate(currentDate.getDate() + 1)
      if (isWeekday(currentDate)) {
        return currentDate
      }
    }
  }
}

export function nextWeekdayInMonths(from: Date, numMonths: number) {
  const currentDate = new Date(from)
  let currentMonth = currentDate.getMonth()
  let counter = 0
  while (counter < numMonths) {
    currentDate.setDate(currentDate.getDate() + 1)
    if (isWeekday(currentDate) && currentDate.getMonth() !== currentMonth) {
      currentMonth = currentDate.getMonth()
      counter++
    }
  }
  return currentDate
}

export function nextWeekdayInWeeks(from: Date, numWeeks: number) {
  const currentDate = closestWeekday(from)
  currentDate.setDate(currentDate.getDate() + 7 * numWeeks)
  return currentDate
}

export function nextWeekdayInDays(date: Date, numDays: number) {
  const currentDate = new Date(date)
  let counter = 0
  while (counter < numDays) {
    currentDate.setDate(currentDate.getDate() + 1)
    if (isWeekday(currentDate)) {
      counter++
    }
  }
  return currentDate
}

export function countWeekdays(from: Date, to = today) {
  const currentDate = new Date(to)
  const endDate = new Date(from)
  currentDate.setHours(0, 0, 0, 0)
  endDate.setHours(0, 0, 0, 0)
  let workDays = 0
  while (currentDate <= endDate) {
    if (isWeekday(currentDate)) {
      workDays++
    }
    currentDate.setDate(currentDate.getDate() + 1)
  }
  return workDays
}

export function getCalendar(year: number, month: number) {
  const startDate = new Date(year, month)
  month = startDate.getMonth()
  year = startDate.getFullYear()
  let startDay = startDate.getDate()
  if (startDay > 1) {
    startDay = 2 - startDay
  } else if (startDay === 0) {
    startDay = -5
  }
  return Array.from({ length: 35 }).map(() => {
    const d = new Date(year, month, startDay++)
    const cd = new CalendarDate(d)
    cd.isSibling = d.getMonth() !== month
    return cd
  })
}

export function formatSeconds(secs: number, placeholder = '…') {
  // short circuit here b/c secs can be NaN here until we get duration in real time and timestamps may not be ready
  if (secs) {
    try {
      // negative seconds is time travel man
      if (secs < 0) {
        secs = 0
      }
      return secs >= 3600 ? dayjs.duration(secs, 's').format('H:mm') : dayjs.duration(secs, 's').format('m:ss')
    } catch (e) {
      return placeholder
    }
  }
  return placeholder
}

export function formatDuration(secs: number) {
  const d = dayjs.duration(secs, 's')
  return secs >= 3600 ? d.format('H[h]m[m]') : secs >= 60 ? d.format('m[m]') : d.format('s[s]')
}

export function formatDate(date: Date, showDay = true) {
  const formatOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    // eslint-disable-next-line
    minute: date.getMinutes() === 0 ? undefined : '2-digit',
    hour12: true,
  }
  if (showDay) {
    formatOptions['weekday'] = 'short'
  }
  const timeFormat = new Intl.DateTimeFormat('default', formatOptions)

  const timeParts = timeFormat.formatToParts(date)
  timeParts[timeParts.length - 1].value = timeParts[timeParts.length - 1].value.toLowerCase()
  timeParts[timeParts.length - 2].value = ''
  if (showDay) {
    timeParts[1].value = ', '
    const calendarDate = new CalendarDate(date)
    if (calendarDate.isToday) {
      timeParts[0].value = 'Today'
    } else if (calendarDate.isTomorrow) {
      timeParts[0].value = 'Tomorrow'
    }
  }
  return timeParts
    .map(function (timePart) {
      return timePart.value
    })
    .join('')
}

export function timeSince(from: Date) {
  const now = new Date()
  const diff = now.getTime() - from.getTime()

  const isToday = new CalendarDate(from).isToday
  if (isToday) {
    return `${Math.round(diff / (1000 * 60 * 60))}h`
  }
  let ago = Math.round(diff / (1000 * 60 * 60 * 24))
  if (ago < 7) {
    return `${ago === 0 ? 1 : ago}d`
  }
  ago = Math.round(diff / (1000 * 60 * 60 * 24 * 7))
  if (ago < 4) {
    return `${ago}w`
  }
  ago = Math.round(diff / (1000 * 60 * 60 * 24 * 30))
  if (ago < 12) {
    return `${ago}m`
  }
  ago = Math.round(diff / (1000 * 60 * 60 * 24 * 365))
  return `${ago}y`
}

export function formatCallTime(date: Date) {
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    // eslint-disable-next-line
    minute: date.getMinutes() === 0 ? undefined : '2-digit',
    hour12: true,
  }
  return new Intl.DateTimeFormat('default', timeOptions).format(date)
}

export function formatCallDate(date: Date) {
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',
  }
  return new Intl.DateTimeFormat('default', dateOptions).format(date) + ' @ ' + formatCallTime(date)
}

export function formatLongDate(dateString: string) {
  const date = dayjs(dateString)
  const formatString = date.isSame(dayjs(), 'year') ? 'MMM D' : 'MMM D, YYYY'
  return date.format(formatString)
}
